// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Slider, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseDatePicker, BasePagination, Loading } from '~/components/common';
// Others
import { DateFormatEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { formatDate, getFullName } from '~/utils/helper/common';
import { getStatisticDailyLoading } from '~/thunks/statistic/statisticThunk';
import { IDailyLoadingAssignment, IQueryParamDailyLoading } from '~/utils/interface/statistic';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE_DASHBOARD, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './DailyLoadingAssignment.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const DailyLoadingAssignment = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateFilter, setDateFilter] = useState<string>(formatDate(new Date(), DateFormatEnum.YYYY_MM_DD));
  const [listData, setListData] = useState<IDailyLoadingAssignment[]>([]);

  const [paramObject, setParamObject] = useState<IQueryParamDailyLoading>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
    startDate: dateFilter,
    endDate: dateFilter,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListDailyLoading(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListDailyLoading = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getStatisticDailyLoading(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setListData(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleChangeData = (date: string) => {
    setDateFilter(date);
    setParamObject({ ...paramObject, startDate: date || EMPTY_STRING, endDate: date || EMPTY_STRING });
  };
  //#endregion Handle Function

  return (
    <Box id='dailyLoadingAssignmentComponent' className={cx('container')}>
      <Stack className={cx('header')}>
        <Stack direction={'column'} spacing={'13px'}>
          <Typography className={cx('title')}>{t('dashboard_title_daily_loading_assignment')}</Typography>

          <Typography className={cx('description')}>
            {t('dashboard_title_daily_loading_assignment_description')}
          </Typography>
        </Stack>

        <BaseDatePicker
          height={36}
          mode='date'
          value={dateFilter}
          onChange={(date: string | { startDate: string; endDate: string }) => handleChangeData(date as string)}
        />
      </Stack>

      <Typography className={cx('textDate')}>
        {t('dashboard_title_daily_loading_assignments_for', {
          value: dateFilter,
        })}
      </Typography>

      <Stack gap={5} flex={1}>
        <Stack className={cx('content')}>
          {listData && listData.length > 0 ? (
            listData.map((item) => (
              <Stack key={item?.id} className={cx('viewItem')}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography className={cx('titleItem')}>{item?.title}</Typography>
                  <Typography className={cx('titlePercent')}>{`${Number(item?.completionRate) || 0}%`}</Typography>
                </Stack>

                <Slider
                  value={item.completionRate}
                  sx={{
                    height: '8px',
                    color: '#0369A1',
                    '& .MuiSlider-thumb': {
                      display: 'none',
                    },
                  }}
                />

                <Stack direction={'row'} alignItems={'center'} gap={2}>
                  <Typography className={cx('titleName')}>{t('dashboard_title_daily_assigned_loader')}</Typography>
                  <Typography className={cx('valueName')}>
                    {Array.isArray(item?.loader) &&
                      item.loader
                        .map((loader) =>
                          getFullName({
                            firstName: loader?.firstName,
                            lastName: loader?.lastName,
                          })
                        )
                        .join(', ')}
                  </Typography>
                </Stack>
              </Stack>
            ))
          ) : (
            <Typography align='center' className={cx('noData')}>
              {t('common_no_data_available', { ns: 'common/base' })}
            </Typography>
          )}
        </Stack>

        {listData.length > 0 && (
          <BasePagination count={pagination?.totalPages} page={pagination?.page} onChange={handlePageChange} />
        )}
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default DailyLoadingAssignment;
